import { useState } from "react";
import { CheckCircle2, AlertCircle, MessageSquare, Reply, Check, RotateCcw, Loader2, Send } from "lucide-react";
import { when } from "@/lib/feedback";

const KIND = {
  approve: { label: "Approved", icon: CheckCircle2, cls: "text-emerald-300" },
  changes: { label: "Changes requested", icon: AlertCircle, cls: "text-amber-300" },
  comment: { label: "Comment", icon: MessageSquare, cls: "text-slate-300" },
  reply: { label: "Team reply", icon: Reply, cls: "text-cyan-300" },
};

export const StatusPill = ({ s, testid }) => {
  const st = s?.status;
  const cls = st === "approved" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : st === "changes" ? "bg-amber-500/15 text-amber-300 border-amber-500/40" : "bg-slate-800 text-slate-400 border-slate-700";
  return <span data-testid={testid} className={`inline-flex items-center text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${cls}`}>{st === "approved" ? "Approved" : st === "changes" ? "Changes" : "Pending"}</span>;
};

const Entry = ({ e, onResolve }) => {
  const k = KIND[e.kind] || KIND.comment, Icon = k.icon, canResolve = onResolve && e.author_role === "client" && e.kind !== "approve";
  return (
    <li data-testid={`feedback-entry-${e.fb_id}`} className={`rounded-md border px-3 py-2 transition-opacity ${e.author_role === "team" ? "border-cyan-500/30 bg-cyan-500/5 ml-4" : "border-slate-800 bg-[#0d1220]"} ${e.resolved ? "opacity-60" : ""}`}>
      <div className="flex items-center gap-1.5 text-[11px] min-w-0">
        <Icon size={12} className={`${k.cls} shrink-0`} /><span className={`font-semibold ${k.cls} shrink-0`}>{k.label}</span>
        <span className="text-slate-300 truncate">· {e.author_name}</span>
        <span className="ml-auto text-slate-500 whitespace-nowrap">{when(e.created_at)}</span>
      </div>
      {e.text && <p className="mt-1 text-sm text-slate-200 whitespace-pre-wrap break-words" data-testid="feedback-entry-text">{e.text}</p>}
      {(e.resolved || canResolve) && (
        <div className="mt-1.5 flex items-center gap-2 text-[11px]">
          {e.resolved && <span className="text-emerald-400 flex items-center gap-1" data-testid="feedback-resolved"><Check size={11} />Resolved</span>}
          {canResolve && (
            <button onClick={() => onResolve(e)} data-testid={`feedback-resolve-${e.fb_id}`} className="ml-auto text-slate-400 hover:text-cyan-300 flex items-center gap-1 transition-colors">
              {e.resolved ? <><RotateCcw size={11} />Reopen</> : <><Check size={11} />Mark resolved</>}
            </button>
          )}
        </div>
      )}
    </li>
  );
};

export const Thread = ({ entries, onResolve, empty = "No comments yet." }) => (
  <ol className="space-y-2" data-testid="feedback-thread">
    {!entries.length && <li className="text-xs text-slate-500" data-testid="feedback-empty">{empty}</li>}
    {entries.map((e) => <Entry key={e.fb_id} e={e} onResolve={onResolve} />)}
  </ol>
);

export const Composer = ({ placeholder, cta = "Send", onSend, testid, autoFocus, tone = "cyan" }) => {
  const [t, setT] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!t.trim() || busy) return;
    setBusy(true);
    try { if ((await onSend(t.trim())) !== false) setT(""); } finally { setBusy(false); }
  };
  const btn = tone === "amber" ? "bg-amber-400 hover:bg-amber-300" : "bg-cyan-500 hover:bg-cyan-400";
  return (
    <div className="space-y-1.5">
      <textarea data-testid={`${testid}-input`} value={t} onChange={(e) => setT(e.target.value)} placeholder={placeholder} autoFocus={autoFocus} maxLength={1000} rows={2}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
        className="w-full rounded-md bg-[#0B0F19] border border-slate-700 focus:border-cyan-500 outline-none px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 resize-y" />
      <button data-testid={`${testid}-send`} onClick={send} disabled={!t.trim() || busy}
        className={`h-9 px-3 rounded-md text-slate-950 text-xs font-semibold flex items-center gap-1.5 ml-auto transition-colors disabled:opacity-40 ${btn}`}>
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}{cta}
      </button>
    </div>
  );
};
